import Ajax from "./base";

/** Pull `data` from the encrypted Ajax envelope; throw on non-success codes. */
const unwrap = (res: any) => {
  if (res == null) return null;
  if (Number(res?.code) === 1 || res?.status === "success") {
    return res.data ?? res;
  }
  const err: any = new Error(res?.msg || res?.message || "Request failed");
  err.response = res;
  throw err;
};

/** base.js defaults payload=null, so TS narrows payload to null|undefined — cast opts. */
const encrypted = (route: string, opts: Record<string, unknown>) =>
  Ajax.encryptedRequest(route, opts as any);

export type AssessmentId = "diet" | "exercise" | "mind";

export type CoachConfigOpts = {
  sessionId?: string | null;
  assessmentCount?: number | null;
};

export const getCoachConfig = async (
  assessmentId: AssessmentId,
  patientId: string,
  opts: CoachConfigOpts = {},
) => {
  const params = new URLSearchParams();
  params.set("patientId", patientId);
  if (opts.sessionId) params.set("sessionId", String(opts.sessionId));
  if (
    opts.assessmentCount != null &&
    Number.isFinite(Number(opts.assessmentCount))
  ) {
    params.set("assessmentCount", String(opts.assessmentCount));
  }
  const res = await encrypted(
    `/assessments/coach/configs/${assessmentId}?${params.toString()}`,
    { method: Ajax.GET, priv: true, withoutBody: true },
  );
  return unwrap(res);
};

export const getCoachSharedProfile = async (patientId: string) => {
  const q = `patientId=${encodeURIComponent(patientId)}`;
  const res = await encrypted(`/assessments/coach/shared-profile?${q}`, {
    method: Ajax.GET,
    priv: true,
    withoutBody: true,
  });
  return unwrap(res);
};

export const saveCoachSharedProfile = async (
  patientId: string,
  answers: Record<string, unknown>,
) => {
  const res = await encrypted(`/assessments/coach/shared-profile`, {
    method: Ajax.POST,
    priv: true,
    payload: { patientId, answers },
  });
  return unwrap(res);
};

export const listCoachSessions = async (
  patientId: string,
  assessmentId: AssessmentId,
) => {
  const q = `patientId=${encodeURIComponent(patientId)}&assessmentId=${encodeURIComponent(assessmentId)}`;
  const res = await encrypted(`/assessments/coach/sessions?${q}`, {
    method: Ajax.GET,
    priv: true,
    withoutBody: true,
  });
  return unwrap(res);
};

export const startCoachSession = async (
  patientId: string,
  assessmentId: AssessmentId,
  opts: { createFollowup?: boolean } = {},
) => {
  const payload: Record<string, unknown> = { patientId, assessmentId };
  if (opts.createFollowup != null) payload.createFollowup = opts.createFollowup;
  const res = await encrypted(`/assessments/coach/sessions`, {
    method: Ajax.POST,
    priv: true,
    payload,
  });
  return unwrap(res);
};

export const patchCoachSession = async (
  sessionId: string,
  body: {
    patientId: string;
    answers?: Record<string, unknown>;
    sectionId?: string;
    questionId?: string;
  },
) => {
  // POST (not PATCH) — avoids CORS/proxy failures seen with PATCH on coach saves
  const res = await encrypted(`/assessments/coach/sessions/${sessionId}`, {
    method: Ajax.POST,
    priv: true,
    payload: body,
  });
  return unwrap(res);
};

export const completeCoachSession = async (
  sessionId: string,
  body: { patientId: string; answers?: Record<string, unknown> },
) => {
  const res = await encrypted(
    `/assessments/coach/sessions/${sessionId}/complete`,
    { method: Ajax.POST, priv: true, payload: body },
  );
  return unwrap(res);
};

export const cancelCoachSession = async (
  sessionId: string,
  patientId: string,
) => {
  const res = await encrypted(
    `/assessments/coach/sessions/${sessionId}?patientId=${encodeURIComponent(patientId)}`,
    {
      method: Ajax.DELETE,
      priv: true,
      payload: { patientId, sessionId },
    },
  );
  return unwrap(res);
};

/** Shared Export PDF — published sessions only (same URL for app + coach). */
export const getSessionPdf = async (sessionId: string, patientId: string) => {
  const q = `patientId=${encodeURIComponent(patientId)}`;
  const res = await encrypted(
    `/assessments/sessions/${sessionId}/pdf?${q}`,
    { method: Ajax.GET, priv: true, withoutBody: true },
  );
  return unwrap(res);
};
